import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import './AuthPages.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const redirect = searchParams.get('redirect') || '/portal';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(redirect);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
      toast.success('If an account exists with that email, a reset link has been sent.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-container">
        {!showForgotPassword ? (
          <>
            <div className="auth-header">
              <h1>Welcome Back</h1>
              <p>Sign into your account to manage your subscription or to purchase one of our lovely heirloom tins.</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form" data-testid="login-form">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="Enter your password"
                  required
                  data-testid="password-input"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                Don&apos;t have an account?{' '}
                <Link to="/register" data-testid="register-link">Create one</Link>
              </p>
              <p style={{marginTop: '0.75rem'}}>
                <button 
                  className="link-btn" 
                  onClick={() => setShowForgotPassword(true)}
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </p>
              <p style={{marginTop: '0.5rem', fontSize: '0.85rem', color: '#999'}}>
                Forgot which email you used?{' '}
                <a href="mailto:admin@bindingthread.com.au" data-testid="forgot-email-link">
                  Contact us
                </a>
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="auth-header">
              <h2>Reset Password</h2>
              <p>Enter your email address and we will send you a link to reset your password.</p>
            </div>

            <form onSubmit={handleForgotPassword} className="auth-form" data-testid="forgot-password-form">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="form-input"
                  placeholder="your@email.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={forgotLoading}
                data-testid="forgot-submit"
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                <button 
                  className="link-btn" 
                  onClick={() => setShowForgotPassword(false)}
                  data-testid="back-to-login-link"
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
